import { memo, useMemo } from 'react'
import NodeListItem from './NodeListItem'
import type { NodeOption } from '../../data/nodeOptions'

interface NodeListProps {
  options: NodeOption[]
  searchQuery: string
  onSelect: (option: NodeOption) => void
}

export interface CategorySection {
  title: string
  key: string // category key from workflow_nodes
  icon?: string
  type?: 'trigger' | 'action' | 'subflow' | 'sticky' // optional type for special categories
  highlight?: boolean
}

// Default categories - matches node_definitions categories from backend
const defaultCategories: CategorySection[] = [
  // Special categories (type-based)
  {
    title: 'Organization',
    key: 'Organization',
    type: 'subflow',
    icon: '🗂️',
    highlight: true,
  },
  // Node definition categories from backend (node_definitions.category)
  {
    title: 'Triggers',
    key: 'Triggers',
    icon: '⚡',
  },
  {
    title: 'Actions',
    key: 'Actions',
    icon: '▶️',
  },
  {
    title: 'Transform',
    key: 'Transform',
    icon: '🔄',
  },
  {
    title: 'Logic',
    key: 'Logic',
    icon: '🔀',
  },
  {
    title: 'Output',
    key: 'Output',
    icon: '📤',
  },
  {
    title: 'Integrations',
    key: 'Integrations',
    icon: '🔌',
  },
  {
    title: 'Otros',
    key: 'Otros',
    icon: '📋',
  },
  // Special categories (type-based)
  {
    title: 'Annotations',
    key: 'Annotations',
    type: 'sticky',
    icon: '📝',
  },
]

// Memoized component - prevents re-renders when parent updates with unchanged props
const NodeList = memo(function NodeList({ options, searchQuery, onSelect }: NodeListProps) {
  // Memoize filtered options to prevent recalculation on every render
  const filtered = useMemo(() =>
    options.filter(
      (o) =>
        o.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.description.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [options, searchQuery]
  );

  if (filtered.length === 0) {
    return (
      <div className="px-3 py-6 text-center text-xs text-muted-foreground">
        No nodes found
      </div>
    )
  }

  // If there's a search query, show flat filtered results
  if (searchQuery.trim()) {
    return (
      <div className="px-1.5 py-3">
        {filtered.map((opt) => (
          <NodeListItem key={opt.id} option={opt} onSelect={onSelect} />
        ))}
      </div>
    )
  }

  // Otherwise, show categorized results
  return (
    <div className="py-1.5">
      {defaultCategories.map((category) => {
        // Filter by type (for special categories) or by category field (for dynamic categories)
        const categoryOptions = filtered.filter((opt) => {
          if (category.type) {
            return opt.type === category.type;
          }
          return opt.category === category.key;
        });

        if (categoryOptions.length === 0) return null

        return (
          <div key={category.key} className="mb-2.5 last:mb-0">
            <div className={`px-3 py-1.5 ${category.highlight ? 'bg-accent/50' : ''}`}>
              <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                {category.icon && <span className="text-xs">{category.icon}</span>}
                <span>{category.title}</span>
              </h3>
            </div>
            <div className="px-1.5 py-0.5">
              {categoryOptions.map((opt) => (
                <NodeListItem key={opt.id} option={opt} onSelect={onSelect} />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
});

export default NodeList;
